import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './ToolCallCard.css';

/** Tooltip shown on the header's orange `tool` icon. */
export const TOOL_CALL_CARD_ICON_LABEL = 'Tool';

/** Card describing one tool call and its result (Figma 3968:28510). */
export type ToolCallCardProps = {
  /** Tool name shown in the header (`name → summary`) and as the arguments label (`name()`). */
  name: string;
  /** Short result summary after the arrow, e.g. `verified · Sarah Johnson`. */
  summary?: ReactNode;
  /** Pre-formatted arguments text (rendered in the code block). Ignored when `argumentsContent` is set. */
  argumentsText?: string;
  /** Pre-formatted response text. Ignored when `responseContent` is set. */
  responseText?: string;
  /** Rich content for the arguments block (e.g. wrapped in `ExpandableText`). */
  argumentsContent?: ReactNode;
  /** Rich content for the response block. */
  responseContent?: ReactNode;
  /** Label above the arguments block. Defaults to `${name}()`. */
  argumentsLabel?: ReactNode;
  /** Label above the response block. Defaults to `response`. */
  responseLabel?: ReactNode;
  /** Uncontrolled initial state. Defaults to the collapsed pill (Figma 3968:28602). */
  defaultExpanded?: boolean;
  /** Controlled expanded state (pair with `onExpandedChange`). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Header toggle labels. */
  showLabel?: string;
  hideLabel?: string;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'className'>;

/**
 * ToolCallCard — an agent tool invocation inside a trajectory.
 *
 * Collapsed (default, Figma 3968:28602): a hugging pill — orange `tool`
 * icon, `name → summary`, and a ghost tiny "Show" toggle — with no divider
 * and only the pill's own 4/8 padding.
 *
 * Expanded (Figma 3968:28510): a full-width card with the header divider,
 * the overlay drop shadow, and two read-only code panes side by side — the
 * call arguments (`name()`) and the tool `response` — in Source Code Pro
 * 12px on the subtle surface. Each pane is resizable in height from its
 * bottom-right handle and scrolls when the content is taller.
 *
 * Hovering the header's `tool` icon shows a "Tool" tooltip.
 */
export function ToolCallCard({
  name,
  summary,
  argumentsText,
  responseText,
  argumentsContent,
  responseContent,
  argumentsLabel,
  responseLabel = 'response',
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  showLabel = 'Show',
  hideLabel = 'Hide',
  className = '',
  ...rest
}: ToolCallCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;

  const toggle = () => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const hasBody =
    argumentsContent !== undefined ||
    responseContent !== undefined ||
    argumentsText !== undefined ||
    responseText !== undefined;
  const showBody = hasBody && isExpanded;

  const classes = [
    'spark-tool-call-card',
    showBody ? 'spark-tool-call-card-expanded' : 'spark-tool-call-card-collapsed',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-tool-name={name} data-expanded={showBody} {...rest}>
      <div className="spark-tool-call-card-header">
        <div className="spark-tool-call-card-title">
          <TooltipTrigger text={TOOL_CALL_CARD_ICON_LABEL} placement="top" className="spark-tool-call-card-icon-trigger">
            <SparkIcon slug="tool" size="small" className="spark-tool-call-card-icon" />
          </TooltipTrigger>
          <span className="spark-tool-call-card-title-text">
            <span className="spark-tool-call-card-name">{name}</span>
            {summary !== undefined && summary !== null && summary !== '' ? (
              <>
                <span className="spark-tool-call-card-arrow" aria-hidden="true">
                  {' → '}
                </span>
                <span className="spark-tool-call-card-summary">{summary}</span>
              </>
            ) : null}
          </span>
        </div>
        {hasBody ? (
          <Button
            variant="ghost"
            size="tiny"
            className="spark-tool-call-card-toggle"
            aria-expanded={isExpanded}
            onClick={toggle}
          >
            {isExpanded ? hideLabel : showLabel}
          </Button>
        ) : null}
      </div>
      {showBody ? (
        <div className="spark-tool-call-card-body">
          <div className="spark-tool-call-card-block">
            <div className="spark-tool-call-card-block-label">{argumentsLabel ?? `${name}()`}</div>
            <div className="spark-tool-call-card-code">
              {argumentsContent !== undefined ? argumentsContent : argumentsText}
            </div>
          </div>
          <div className="spark-tool-call-card-block">
            <div className="spark-tool-call-card-block-label">{responseLabel}</div>
            <div className="spark-tool-call-card-code">
              {responseContent !== undefined ? responseContent : responseText}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
